import { useFloating, offset, flip, size, autoUpdate } from '@floating-ui/react';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './GridMenu.module.scss.js';
import styles$1 from '../Menu.module.scss.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useA11y } from '../../provider/A11yProvider.js';
import { Dialog } from '../../dialog/Dialog.js';
import { UNSAFE_Icon } from '../../icon/Icon.js';
import { Text } from '../../text/Text.js';
import classNames from 'clsx';
import { MdChevronRight } from '../../node_modules/react-icons/md/index.js';

function GridSubmenu(props) {
  const {
    active,
    children,
    disabled,
    icon,
    isOpen,
    noScroll,
    primary,
    secondary,
    size: size$1,
    tag,
    value,
    variant = 'default',
    onSelect
  } = props;
  const {
    portalTarget
  } = useA11y();
  const [referenceElement, setReferenceElement] = useState(null);
  const {
    floatingStyles,
    refs,
    elements,
    update
  } = useFloating({
    elements: {
      reference: referenceElement
    },
    open: isOpen,
    placement: 'right-start',
    strategy: 'fixed',
    middleware: [offset({
      mainAxis: 0,
      crossAxis: -4
    }), flip({
      crossAxis: false
    }), size({
      apply(_ref) {
        let {
          availableHeight,
          elements
        } = _ref;
        Object.assign(elements.floating.style, {
          maxHeight: `${Math.max(50, availableHeight - 8)}px`
        });
      }
    })]
  });
  useEffect(() => {
    if (isOpen && elements.reference && elements.floating) {
      const cleanup = autoUpdate(elements.reference, elements.floating, update);
      return cleanup;
    }
  }, [isOpen, elements, update]);
  const menuRef = useRef(null);
  const hasOverflow = menuRef.current && menuRef.current.clientHeight < menuRef.current.scrollHeight;
  const optionClassName = classNames(styles['item'], disabled && styles['disabled'], isOpen && styles['active'], active && styles['focus'], styles[`variant-${variant}`], styles[`size-${size$1}`]);
  const dialogClassName = classNames('dsx-component', styles$1['dialog']);
  const menuClassName = classNames(styles$1['menu'], styles$1[`size-${size$1}`], noScroll && styles$1['no-scroll'], hasOverflow && styles$1['has-overflow']);
  useEffect(() => {
    if (active) {
      referenceElement?.scrollIntoView({
        block: 'nearest'
      });
    }
    // This effect only needs to run when the active state of the element has changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  function handleSelect() {
    if (!disabled) {
      onSelect(value);
    }
  }
  function preventBlur(event) {
    // Note: this will prevent :active styling on menu items in Firefox:
    // https://bugzilla.mozilla.org/show_bug.cgi?id=771241
    event.preventDefault();
  }
  const content = /*#__PURE__*/jsxs(Fragment, {
    children: [icon && /*#__PURE__*/jsx("div", {
      className: styles['icon-start'],
      children: icon && /*#__PURE__*/jsx("span", {
        children: /*#__PURE__*/jsx(UNSAFE_Icon, {
          icon: icon
        })
      })
    }), /*#__PURE__*/jsx(Text, {
      className: styles['primary'],
      variant: size$1 === 'small' ? 'menu-item-small' : 'menu-item',
      children: primary
    }), secondary != null && /*#__PURE__*/jsx(Text, {
      className: styles['secondary'],
      variant: size$1 === 'small' ? 'menu-description-small' : 'menu-description',
      children: secondary
    }), /*#__PURE__*/jsx("div", {
      className: styles['icon-end'],
      children: /*#__PURE__*/jsx("span", {
        children: /*#__PURE__*/jsx(UNSAFE_Icon, {
          icon: MdChevronRight
        })
      })
    })]
  });
  const anchorEl = /*#__PURE__*/jsx("div", {
    className: optionClassName,
    ref: setReferenceElement,
    tabIndex: -1,
    onClick: handleSelect,
    "data-dsx--submenu": tag,
    children: content
  });
  return /*#__PURE__*/jsxs(Fragment, {
    children: [anchorEl, portalTarget && /*#__PURE__*/createPortal(/*#__PURE__*/jsx(Dialog, {
      focusOnOpen: false,
      isOpen: isOpen,
      className: dialogClassName,
      ref: refs.setFloating,
      style: floatingStyles,
      onMouseDown: preventBlur,
      "data-dsx--submenu--popover": tag,
      children: /*#__PURE__*/jsx("div", {
        className: menuClassName,
        ref: menuRef,
        children: children
      })
    }), portalTarget)]
  });
}

export { GridSubmenu };
